import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  Trophy,
  Timer,
  User,
  Users,
  NotebookPen,
  ChevronDown,
  BookOpen,
  RotateCcw,
  LogOut,
} from "@/lib/icons";
import GameLayout from "@/games/shared/GameLayout";
import GameMenu from "@/games/shared/GameMenu";
import PlayerSetup from "@/games/shared/PlayerSetup";
import Scoreboard from "@/games/shared/Scoreboard";
import TurnTimer from "@/games/shared/TurnTimer";
import MatchResultDialog from "@/games/shared/MatchResultDialog";
import { useHotSeatMatch, type PlayerId } from "@/games/shared/useHotSeatMatch";
import { play } from "@/games/shared/sound";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import GlasswareIcon from "@/games/glasswareId/GlasswareIcon";
import {
  generateQuestion,
  scoreAnswer,
  GLASSWARE_FAST_THRESHOLD_S,
  GLASSWARE_POINTS_FAST,
  GLASSWARE_POINTS_SLOW,
  GLASSWARE_TURN_SECONDS,
  type GlasswareOutcome,
  type GlasswareQuestion,
} from "@/games/glasswareId/engine";
import {
  GLASSWARE_CATEGORY_LABEL,
  type GlasswareCategory,
} from "@/games/glasswareId/items";

const QUESTIONS_PER_MATCH = 15;
const RESULT_PAUSE_MS = 1700;
const SOLO_BEST_KEY = "elementpad:glassware-id:best";

type Mode = "solo" | "hot-seat";

type HistoryEntry = {
  id: string;
  name: string;
  category: GlasswareCategory;
  why: string;
  outcome: GlasswareOutcome;
};

const readBest = (): number => {
  try {
    const raw = window.localStorage.getItem(SOLO_BEST_KEY);
    return raw ? Number(raw) || 0 : 0;
  } catch {
    return 0;
  }
};
const writeBest = (score: number) => {
  try {
    window.localStorage.setItem(SOLO_BEST_KEY, String(score));
  } catch {
    /* ignore */
  }
};

/**
 * Glassware ID - identify lab apparatus from inline SVG icons.
 *
 * Two modes:
 *  - Solo: 15 questions, race the clock. Personal best in localStorage.
 *  - Hot-seat 2-player: alternating questions, highest total wins.
 *
 * Shared scoring: fast correct = 15, slow correct = 8, wrong/timeout = 0.
 * Per-question explanations and an end-of-match recap with per-category
 * accuracy match the pattern from Lab Safety Showdown.
 */
export default function GlasswareId() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode | null>(null);

  if (!mode) return <ModeSelect onPick={setMode} />;
  if (mode === "solo") {
    return (
      <SoloMatch
        onChangeMode={() => setMode(null)}
        onExit={() => navigate("/play")}
      />
    );
  }
  return (
    <HotSeatMatch
      onChangeMode={() => setMode(null)}
      onExit={() => navigate("/play")}
    />
  );
}

// ---------------- mode select ----------------

function ModeSelect({ onPick }: { onPick: (m: Mode) => void }) {
  return (
    <GameLayout
      title="Glassware ID"
      subtitle="Name the lab apparatus before the clock runs out. 15 questions per round."
      headerActions={<GameMenu gameKey="glassware-id" />}
    >
      <div className="max-w-md mx-auto rounded-xl border border-border bg-card p-6">
        <div className="flex items-center gap-2 mb-1">
          <NotebookPen size={16} className="text-primary" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-foreground">Choose a mode</h2>
        </div>
        <p className="text-sm text-muted-foreground mb-5">
          Solo for high scores, or hot-seat to compete head-to-head on one device.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
          <button
            type="button"
            onClick={() => onPick("solo")}
            className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-2 mb-1">
              <User size={14} className="text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Solo</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              One player. Best score saved.
            </p>
          </button>
          <button
            type="button"
            onClick={() => onPick("hot-seat")}
            className="text-left p-4 rounded-md border border-border bg-background hover:border-primary/60 hover:bg-accent/40 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <div className="flex items-center gap-2 mb-1">
              <Users size={14} className="text-primary" aria-hidden="true" />
              <span className="text-sm font-semibold text-foreground">Hot-seat 2P</span>
            </div>
            <p className="text-xs text-muted-foreground leading-snug">
              Alternate questions. Highest score wins.
            </p>
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-3">
          {GLASSWARE_TURN_SECONDS}s per question · ≤{GLASSWARE_FAST_THRESHOLD_S}s ={" "}
          {GLASSWARE_POINTS_FAST} pts · slower correct = {GLASSWARE_POINTS_SLOW} pts.
        </p>
      </div>
    </GameLayout>
  );
}

// ---------------- solo ----------------

type SoloPhase =
  | {
      kind: "playing";
      questionIndex: number;
      question: GlasswareQuestion;
      startedAt: number;
      score: number;
    }
  | {
      kind: "result";
      questionIndex: number;
      question: GlasswareQuestion;
      chosenIndex: number | null;
      score: number;
      lastAward: number;
      outcome: GlasswareOutcome;
    }
  | { kind: "finished"; score: number; best: number };

function makeFirstQuestion(score: number): SoloPhase {
  const q = generateQuestion(Math.random, new Set());
  return {
    kind: "playing",
    questionIndex: 0,
    question: q,
    startedAt: performance.now(),
    score,
  };
}

function SoloMatch({
  onChangeMode,
  onExit,
}: {
  onChangeMode: () => void;
  onExit: () => void;
}) {
  const [phase, setPhase] = useState<SoloPhase>(() => makeFirstQuestion(0));
  const askedRef = useRef<Set<string>>(new Set());
  const historyRef = useRef<HistoryEntry[]>([]);
  const advanceTimer = useRef<number | null>(null);

  useEffect(() => {
    if (phase.kind === "playing") askedRef.current.add(phase.question.id);
  }, [phase]);

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const handleAnswer = useCallback(
    (chosenIndex: number | null) => {
      if (phase.kind !== "playing") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosenIndex, phase.question, elapsed);
      const nextScore = phase.score + points;
      historyRef.current.push({
        id: phase.question.item.id,
        name: phase.question.item.name,
        category: phase.question.item.category,
        why: phase.question.item.why,
        outcome,
      });
      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");

      setPhase({
        kind: "result",
        questionIndex: phase.questionIndex,
        question: phase.question,
        chosenIndex,
        score: nextScore,
        lastAward: points,
        outcome,
      });

      advanceTimer.current = window.setTimeout(() => {
        const nextIndex = phase.questionIndex + 1;
        if (nextIndex >= QUESTIONS_PER_MATCH) {
          const best = readBest();
          if (nextScore > best) writeBest(nextScore);
          play(nextScore > best ? "win" : "click");
          setPhase({ kind: "finished", score: nextScore, best: Math.max(best, nextScore) });
          return;
        }
        const nextQ = generateQuestion(Math.random, askedRef.current);
        askedRef.current.add(nextQ.id);
        setPhase({
          kind: "playing",
          questionIndex: nextIndex,
          question: nextQ,
          startedAt: performance.now(),
          score: nextScore,
        });
      }, RESULT_PAUSE_MS);
    },
    [phase],
  );

  const onExpire = useCallback(() => handleAnswer(null), [handleAnswer]);

  const restart = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    setPhase(makeFirstQuestion(0));
  };

  if (phase.kind === "finished") {
    return (
      <GameLayout
        title="Glassware ID"
        subtitle="Solo · 15 questions"
        headerActions={<GameMenu gameKey="glassware-id" onReset={restart} resetLabel="Restart" />}
      >
        <MatchRecap
          headline="Round complete"
          subhead={
            phase.score > phase.best - phase.score
              ? `Final score: ${phase.score}`
              : `Final score: ${phase.score}`
          }
          stat={
            <div className="text-center">
              <Trophy size={28} className="mx-auto text-primary mb-2" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">Final score</p>
              <p className="text-4xl font-bold font-mono text-foreground tabular-nums mb-1">
                {phase.score}
              </p>
              <p className="text-xs text-muted-foreground">
                Personal best: <span className="font-mono">{phase.best}</span>
              </p>
            </div>
          }
          history={historyRef.current}
          onExit={onExit}
          onChangeMode={onChangeMode}
          onAgain={restart}
          againLabel="Play again"
        />
      </GameLayout>
    );
  }

  const active = phase.kind === "playing" ? phase : null;
  const result = phase.kind === "result" ? phase : null;
  const q = active?.question ?? result!.question;
  const idx = active?.questionIndex ?? result!.questionIndex;
  const score = active?.score ?? result!.score;

  return (
    <GameLayout
      title="Glassware ID"
      subtitle="Solo · 15 questions"
      headerActions={<GameMenu gameKey="glassware-id" onReset={restart} resetLabel="Restart" />}
    >
      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat label="Question" value={`${idx + 1} / ${QUESTIONS_PER_MATCH}`} />
        <Stat label="Score" value={String(score)} mono />
        <div className="rounded-lg border border-border bg-card p-3 flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground">Time</span>
          {active ? (
            <TurnTimer
              key={`solo-${q.id}-${idx}`}
              seconds={GLASSWARE_TURN_SECONDS}
              onExpire={onExpire}
              size={40}
            />
          ) : (
            <Timer size={18} className="text-muted-foreground" aria-hidden="true" />
          )}
        </div>
      </div>

      <QuestionCard
        question={q}
        chosenIndex={result?.chosenIndex ?? null}
        onChoose={(i) => active && handleAnswer(i)}
        revealed={!!result}
      />

      {result && (
        <ResultBanner
          outcome={result.outcome}
          award={result.lastAward}
          name={q.item.name}
          use={q.item.use}
          why={q.item.why}
        />
      )}
    </GameLayout>
  );
}

// ---------------- hot-seat ----------------

type HotSeatPhase =
  | { kind: "answering"; question: GlasswareQuestion; startedAt: number }
  | {
      kind: "result";
      question: GlasswareQuestion;
      chosenIndex: number | null;
      lastAward: number;
      outcome: GlasswareOutcome;
    }
  | { kind: "handoff"; nextPlayer: PlayerId };

function HotSeatMatch({
  onChangeMode,
  onExit,
}: {
  onChangeMode: () => void;
  onExit: () => void;
}) {
  const match = useHotSeatMatch(0);
  const [phase, setPhase] = useState<HotSeatPhase | null>(null);
  const askedRef = useRef<Set<string>>(new Set());
  const historyRef = useRef<HistoryEntry[]>([]);
  const advanceTimer = useRef<number | null>(null);
  const TOTAL_TURNS = QUESTIONS_PER_MATCH * 2; // each player gets 15 questions

  useEffect(() => {
    return () => {
      if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    };
  }, []);

  const startMatch = useCallback(
    (p1: string, p2: string) => {
      askedRef.current = new Set();
      historyRef.current = [];
      match.start(p1, p2);
      setPhase({ kind: "handoff", nextPlayer: 0 });
    },
    [match],
  );

  const beginTurn = useCallback(() => {
    const q = generateQuestion(Math.random, askedRef.current);
    askedRef.current.add(q.id);
    setPhase({ kind: "answering", question: q, startedAt: performance.now() });
  }, []);

  const submitAnswer = useCallback(
    (chosenIndex: number | null) => {
      if (!phase || phase.kind !== "answering") return;
      const elapsed = performance.now() - phase.startedAt;
      const { points, outcome } = scoreAnswer(chosenIndex, phase.question, elapsed);

      const current = match.state.currentTurn;
      const newScore = match.state.stats[current] + points;
      match.setStat(current, newScore);

      historyRef.current.push({
        id: phase.question.item.id,
        name: phase.question.item.name,
        category: phase.question.item.category,
        why: phase.question.item.why,
        outcome,
      });

      if (outcome === "fast" || outcome === "slow") play("correct");
      else if (outcome === "wrong") play("wrong");

      setPhase({
        kind: "result",
        question: phase.question,
        chosenIndex,
        lastAward: points,
        outcome,
      });

      advanceTimer.current = window.setTimeout(() => {
        if (match.state.turnNumber >= TOTAL_TURNS) {
          const a = match.state.stats[0] + (current === 0 ? points : 0);
          const b = match.state.stats[1] + (current === 1 ? points : 0);
          const winner: PlayerId | null = a === b ? null : a > b ? 0 : 1;
          play(winner === null ? "click" : "win");
          match.finish(winner);
          return;
        }
        match.nextTurn();
        const next: PlayerId = current === 0 ? 1 : 0;
        setPhase({ kind: "handoff", nextPlayer: next });
      }, RESULT_PAUSE_MS);
    },
    [phase, match, TOTAL_TURNS],
  );

  const onExpire = useCallback(() => submitAnswer(null), [submitAnswer]);

  const handleRematch = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.start(match.state.players[0].name, match.state.players[1].name);
    setPhase({ kind: "handoff", nextPlayer: 0 });
  };
  const handleChangePlayers = () => {
    if (advanceTimer.current) window.clearTimeout(advanceTimer.current);
    askedRef.current = new Set();
    historyRef.current = [];
    match.reset();
    setPhase(null);
  };

  if (match.state.status === "setup") {
    return (
      <GameLayout
        title="Glassware ID"
        subtitle={`Hot-seat · ${QUESTIONS_PER_MATCH} questions each`}
        headerActions={<GameMenu gameKey="glassware-id" />}
      >
        <PlayerSetup
          title="Set up the match"
          description="Two players alternate questions on the same device. Highest total wins."
          ctaLabel="Start"
          onStart={startMatch}
        />
        <div className="max-w-md mx-auto mt-3 text-center">
          <button
            type="button"
            onClick={onChangeMode}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Change mode
          </button>
        </div>
      </GameLayout>
    );
  }

  const matchOver = match.state.status === "finished";
  const current = match.state.players[match.state.currentTurn];
  const turnsRemaining = Math.max(0, TOTAL_TURNS - match.state.turnNumber + 1);

  return (
    <GameLayout
      title="Glassware ID"
      subtitle="Hot-seat 2P"
      headerActions={<GameMenu gameKey="glassware-id" onReset={handleRematch} resetLabel="Restart" />}
    >
      <div className="mb-5">
        <Scoreboard state={match.state} statLabel="Score" />
      </div>

      {phase?.kind === "handoff" && !matchOver && (
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <p className="text-sm text-muted-foreground mb-1">Pass the device to:</p>
          <p className="text-2xl font-bold text-foreground mb-1">{current.name}</p>
          <p className="text-[11px] text-muted-foreground mb-4">
            Turn {match.state.turnNumber} of {TOTAL_TURNS} · {turnsRemaining} left
          </p>
          <Button onClick={beginTurn} size="lg">
            <NotebookPen size={14} className="mr-1.5" aria-hidden="true" />
            I'm ready
          </Button>
        </div>
      )}

      {phase?.kind === "answering" && !matchOver && (
        <>
          <div className="flex items-center justify-between mb-3">
            <span className="text-[11px] text-muted-foreground">{current.name}'s turn</span>
            <TurnTimer
              key={`hs-${phase.question.id}-${match.state.turnNumber}`}
              seconds={GLASSWARE_TURN_SECONDS}
              onExpire={onExpire}
              size={44}
            />
          </div>
          <QuestionCard
            question={phase.question}
            chosenIndex={null}
            onChoose={submitAnswer}
            revealed={false}
          />
        </>
      )}

      {phase?.kind === "result" && !matchOver && (
        <>
          <QuestionCard
            question={phase.question}
            chosenIndex={phase.chosenIndex}
            onChoose={() => undefined}
            revealed
          />
          <ResultBanner
            outcome={phase.outcome}
            award={phase.lastAward}
            name={phase.question.item.name}
            use={phase.question.item.use}
            why={phase.question.item.why}
          />
        </>
      )}

      <MatchResultDialog
        state={match.state}
        open={matchOver}
        onRematch={handleRematch}
        onChangePlayers={handleChangePlayers}
        onExit={onExit}
        detail={`${QUESTIONS_PER_MATCH} questions each. Higher total wins.`}
      />
    </GameLayout>
  );
}

// ---------------- shared sub-components ----------------

function Stat({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="rounded-lg border border-border bg-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
        {label}
      </div>
      <div
        className={`text-base font-semibold text-foreground ${
          mono ? "font-mono tabular-nums" : ""
        }`}
      >
        {value}
      </div>
    </div>
  );
}

function QuestionCard({
  question,
  chosenIndex,
  onChoose,
  revealed,
}: {
  question: GlasswareQuestion;
  chosenIndex: number | null;
  onChoose: (i: number) => void;
  revealed: boolean;
}) {
  return (
    <div className="rounded-xl border border-primary/40 bg-card p-5 mb-3">
      <div className="flex flex-col items-center mb-4">
        <div className="rounded-lg bg-background border border-border p-4 mb-2 text-primary">
          <GlasswareIcon id={question.item.id} size={140} />
        </div>
        <h2 className="text-sm font-medium text-muted-foreground">
          What is this piece of apparatus?
        </h2>
      </div>
      <div
        className="grid grid-cols-1 sm:grid-cols-2 gap-2"
        role="group"
        aria-label="Answer choices"
      >
        {question.choices.map((choice, i) => {
          const isCorrect = i === question.correctIndex;
          const isChosen = i === chosenIndex;
          const stateClasses = revealed
            ? isCorrect
              ? "border-primary bg-primary/10 text-foreground"
              : isChosen
                ? "border-destructive/60 bg-destructive/10 text-foreground"
                : "border-border bg-background text-muted-foreground"
            : "border-border bg-background hover:border-primary/60 hover:bg-accent/40";
          return (
            <button
              key={i}
              type="button"
              onClick={() => !revealed && onChoose(i)}
              disabled={revealed}
              className={`text-left p-3 rounded-md border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${stateClasses} disabled:cursor-default`}
            >
              <span className="inline-flex items-center justify-center w-5 h-5 mr-2 rounded text-[10px] font-bold bg-muted text-muted-foreground">
                {String.fromCharCode(65 + i)}
              </span>
              <span className="text-sm">{choice}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ResultBanner({
  outcome,
  award,
  name,
  use,
  why,
}: {
  outcome: GlasswareOutcome;
  award: number;
  name: string;
  use: string;
  why: string;
}) {
  const positive = outcome === "fast" || outcome === "slow";
  return (
    <div
      role="status"
      aria-live="polite"
      className={`rounded-md border p-3 ${
        positive
          ? "border-primary/40 bg-primary/5"
          : "border-destructive/30 bg-destructive/5"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        {positive ? (
          <CheckCircle2 size={14} className="text-primary" aria-hidden="true" />
        ) : (
          <XCircle size={14} className="text-destructive" aria-hidden="true" />
        )}
        <span className="text-sm font-semibold text-foreground">
          {outcome === "fast" && `Fast & correct! +${award} pts`}
          {outcome === "slow" && `Correct. +${award} pts`}
          {outcome === "wrong" && `Wrong - it's a ${name}`}
          {outcome === "timeout" && `Time's up - it's a ${name}`}
        </span>
        {positive && (
          <Badge variant="secondary" className="text-[10px] ml-auto">
            {outcome === "fast" ? "Speed bonus" : "Standard"}
          </Badge>
        )}
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">{use}</p>
      <WhyCorrect text={why} defaultOpen={!positive} />
    </div>
  );
}

function WhyCorrect({ text, defaultOpen = false }: { text: string; defaultOpen?: boolean }) {
  return (
    <details
      className="mt-2 group rounded-md border border-border/60 bg-background/40 open:bg-background/60"
      open={defaultOpen}
    >
      <summary className="cursor-pointer list-none px-2.5 py-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-foreground hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-md">
        <BookOpen size={12} className="text-primary" aria-hidden="true" />
        <span>Why this is correct</span>
        <ChevronDown
          size={12}
          className="ml-auto text-muted-foreground transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>
      <p className="px-2.5 pb-2.5 pt-0.5 text-xs text-muted-foreground leading-relaxed">
        {text}
      </p>
    </details>
  );
}

// ---------------- recap ----------------

function MatchRecap({
  headline,
  subhead,
  stat,
  history,
  onExit,
  onChangeMode,
  onAgain,
  againLabel,
}: {
  headline: string;
  subhead: string;
  stat: React.ReactNode;
  history: HistoryEntry[];
  onExit: () => void;
  onChangeMode: () => void;
  onAgain: () => void;
  againLabel: string;
}) {
  const total = history.length;
  const correct = history.filter(
    (h) => h.outcome === "fast" || h.outcome === "slow",
  ).length;
  const wrong = history.filter((h) => h.outcome === "wrong").length;
  const timeout = history.filter((h) => h.outcome === "timeout").length;
  const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;

  // Per-category breakdown.
  const byCategory = useMemo(() => {
    const map = new Map<GlasswareCategory, { c: number; t: number }>();
    for (const h of history) {
      const cur = map.get(h.category) ?? { c: 0, t: 0 };
      cur.t += 1;
      if (h.outcome === "fast" || h.outcome === "slow") cur.c += 1;
      map.set(h.category, cur);
    }
    return Array.from(map.entries()).sort((a, b) => b[1].t - a[1].t);
  }, [history]);

  // Top 3 lessons: prefer missed items, then dedupe by id; fall back to correct.
  const topLessons = useMemo(() => {
    const seen = new Set<string>();
    const lessons: HistoryEntry[] = [];
    for (const h of history) {
      if (h.outcome === "wrong" || h.outcome === "timeout") {
        if (seen.has(h.id)) continue;
        seen.add(h.id);
        lessons.push(h);
        if (lessons.length >= 3) return lessons;
      }
    }
    for (const h of history) {
      if (lessons.length >= 3) break;
      if (seen.has(h.id)) continue;
      seen.add(h.id);
      lessons.push(h);
    }
    return lessons;
  }, [history]);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="sr-only">{headline}</h2>
        {stat}
      </div>

      <div className="rounded-xl border border-border bg-card p-5">
        <h3 className="text-sm font-semibold text-foreground mb-3">Performance</h3>
        <div className="grid grid-cols-3 gap-2 mb-4">
          <BreakdownStat label="Correct" value={correct} tone="positive" />
          <BreakdownStat label="Wrong" value={wrong} tone="negative" />
          <BreakdownStat label="Timeout" value={timeout} tone="muted" />
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Accuracy
            </span>
            <span className="text-sm font-mono tabular-nums text-foreground">{accuracy}%</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${accuracy}%` }}
            />
          </div>
        </div>

        {byCategory.length > 0 && (
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {byCategory.map(([cat, { c, t }]) => (
              <div
                key={cat}
                className="flex items-center justify-between text-xs px-3 py-2 rounded-md border border-border bg-background"
              >
                <span className="text-muted-foreground">
                  {GLASSWARE_CATEGORY_LABEL[cat]}
                </span>
                <span className="font-mono tabular-nums text-foreground">
                  {c}/{t}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {topLessons.length > 0 && (
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen size={14} className="text-primary" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-foreground">Top lessons</h3>
          </div>
          <ul className="space-y-2">
            {topLessons.map((l) => (
              <li
                key={l.id}
                className="rounded-md border border-border bg-background p-3"
              >
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <span className="text-sm font-semibold text-foreground">{l.name}</span>
                  <Badge
                    variant="secondary"
                    className="text-[10px] uppercase tracking-wider"
                  >
                    {GLASSWARE_CATEGORY_LABEL[l.category]}
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed">{l.why}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <Button variant="outline" onClick={onExit}>
          <LogOut size={14} className="mr-1.5" aria-hidden="true" />
          Quit
        </Button>
        <Button variant="outline" onClick={onChangeMode}>
          Change mode
        </Button>
        <Button onClick={onAgain}>
          <RotateCcw size={14} className="mr-1.5" aria-hidden="true" />
          {againLabel}
        </Button>
      </div>
    </div>
  );
}

function BreakdownStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: "positive" | "negative" | "muted";
}) {
  const toneClass =
    tone === "positive"
      ? "text-primary"
      : tone === "negative"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className="rounded-md border border-border bg-background p-3 text-center">
      <div className={`text-2xl font-bold font-mono tabular-nums ${toneClass}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">
        {label}
      </div>
    </div>
  );
}
